/**
 * User Profile Hooks
 * Hooks for fetching and updating user profile information
 */

import { apiClient, API_BASE_URL } from '@shared/lib/api/client'
import { API_ENDPOINTS } from '@shared/lib/api/endpoints'
import type { User } from '@shared/types'
import { tokenStorage } from '@shared/lib/storage'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

// ============================================================================
// Query Keys
// ============================================================================

export const userProfileKeys = {
  all: ['userProfile'] as const,
  profile: () => [...userProfileKeys.all, 'profile'] as const,
}

// ============================================================================
// Types
// ============================================================================

export interface UpdateProfileData {
  firstName: string
  lastName: string
  email: string
  phone: string
  cityId?: string
  gender?: 'male' | 'female'
}

export interface ApiUserProfile {
  _id: string
  name: string
  lastName: string
  email: string
  phoneNumber: string
  city?: string
  cityId?: string
  gender?: 'male' | 'female' | null
  isProfessional?: boolean
  isAccountValidated?: boolean
}

// ============================================================================
// Queries
// ============================================================================

/**
 * Get current user's profile information
 */
export function useUserProfile() {
  return useQuery({
    queryKey: userProfileKeys.profile(),
    queryFn: async () => {
      const apiProfile = await apiClient.get<ApiUserProfile>(API_ENDPOINTS.users.profile)

      // Map API response to User type
      const user: User = {
        id: apiProfile._id,
        firstName: apiProfile.name,
        lastName: apiProfile.lastName,
        email: apiProfile.email,
        phone: apiProfile.phoneNumber,
        city: apiProfile.city,
        cityId: apiProfile.cityId,
        gender: apiProfile.gender ?? null,
        isEmailValidated: apiProfile.isAccountValidated ?? false,
        isPhoneValidated: apiProfile.isAccountValidated ?? false,
        isProfessional: apiProfile.isProfessional || false,
        isAccountValidated: apiProfile.isAccountValidated ?? false,
        createdAt: '',
        updatedAt: '',
      }

      return user;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// ============================================================================
// Mutations
// ============================================================================

/**
 * Update user profile information
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateProfileData) => {
      const payload: Record<string, string> = {
        name: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: data.phone.replace(/\D/g, ''),
      }

      if (data.cityId) {
        payload.city = data.cityId
      }

      if (data.gender) {
        payload.gender = data.gender
      }

      return await apiClient.post(API_ENDPOINTS.users.updateAccountInformation, payload)
    },
    onSuccess: () => {
      // Invalidate and refetch profile data
      void queryClient.invalidateQueries({ queryKey: userProfileKeys.profile() })
    },
  })
}

/**
 * Upload user profile picture
 * Backend endpoint: POST /api/users/updateAccountInformation (with file)
 */
export function useUploadUserProfilePicture() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('profilePicture', file)

      const token = tokenStorage.getToken()
      const headers: Record<string, string> = {}

      // Add Authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.users.updateAccountInformation}`,
        {
          method: 'POST',
          credentials: 'include',
          headers,
          body: formData,
        }
      )

      interface UploadResponse {
        success: boolean
        data?: unknown
        message?: { error?: string }
      }
      const json = (await response.json()) as UploadResponse
      if (!json.success) {
        throw new Error(json.message?.error || 'Failed to upload profile picture')
      }

      return json.data
    },
    onSuccess: () => {
      // Invalidate profile to refetch with new picture
      void queryClient.invalidateQueries({ queryKey: userProfileKeys.profile() })
    },
  })
}
